import { getPipelineIndex } from './matterPipeline.js';
import { isPaidBeforeAssignment } from './paidBeforeAssignment.js';
import { formatMatterMoney } from './matterCurrency.js';

/**
 * Show "Notify attorney" once counsel is involved (Assigned+) and the
 * caller has detected unsaved field changes.
 */
export function shouldOfferAttorneyNotify(matter) {
  if (!matter) return false;
  if (matter.Attorney_ID || matter.Attorney_Name) return true;
  return getPipelineIndex(matter.Status) >= 1;
}

export function hasNotifiableFieldChanges(changeSummary) {
  return Array.isArray(changeSummary?.changes) && changeSummary.changes.length > 0;
}

/**
 * Client message body posted to Attorney communication after a demand save.
 */
export function buildAttorneyUpdateMessage(changeSummary) {
  const raw = String(changeSummary?.summary || '').trim();
  if (!raw || !hasNotifiableFieldChanges(changeSummary)) return '';
  const detail = raw.replace(/^Updated\s+/i, '');
  return `I updated this demand: ${detail}.`;
}

function formatMoneyLabel(value, currencyOrMatter = 'USD') {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return formatMatterMoney(amount, currencyOrMatter, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * True when this payment left an outstanding balance (partial pay).
 * Prefer explicit fullyPaid=false, then a positive remaining balance.
 */
export function isPartialPaymentResolution(resolution = {}) {
  if (resolution?.fullyPaid === false) return true;
  if (resolution?.fullyPaid === true) return false;
  const remaining = Number(resolution?.balanceRemaining);
  return Number.isFinite(remaining) && remaining > 0;
}

/**
 * Client message body posted when recording that the debtor paid.
 * @param {{ waived?: boolean, status?: string, fullyPaid?: boolean, amountPaid?: number, balanceRemaining?: number }|null|undefined} resolution
 */
export function buildDebtorPaymentAttorneyMessage(resolution = {}) {
  const reference = String(resolution?.payment?.reference || resolution?.reference || '').trim();
  const referenceSuffix = reference ? ` Reference #${reference}.` : '';
  const currency =
    resolution?.payment?.currency ||
    resolution?.currency ||
    resolution?.Currency ||
    'USD';

  // Partial payments must never use the "paid before assignment / no fee" copy.
  if (isPartialPaymentResolution(resolution)) {
    const paidLabel = formatMoneyLabel(resolution.amountPaid, currency);
    const remainingLabel = formatMoneyLabel(resolution.balanceRemaining, currency);
    if (paidLabel && remainingLabel) {
      return (
        `I recorded a partial debtor payment of ${paidLabel} on this demand. ` +
        `${remainingLabel} remains outstanding.${referenceSuffix}`
      );
    }
    return (
      `I recorded a partial debtor payment on this demand. The balance is still outstanding.${referenceSuffix}`
    );
  }

  const closedBeforeAssignment =
    Boolean(resolution?.waived) || isPaidBeforeAssignment(resolution?.status);
  if (closedBeforeAssignment) {
    return (
      'I recorded a debtor payment on this demand. The invoice was paid before assignment, ' +
      `so the matter closed with no per-demand fee.${referenceSuffix}`
    );
  }

  return (
    'I recorded a debtor payment on this demand. Please treat the invoice as paid ' +
    `and close out any remaining collection work.${referenceSuffix}`
  );
}
